<template lang="pug">
div.wrap-item-asset-model-preview.w-full.h-full
  div.wrap-canvas
    div(ref="wrapCanvas")
  div(v-if="isLoading").wrap-loading.f.fh.w-full.h-full
    div
      div.f.fc
        v-progress-circular(:color="pallete.$active_color" indeterminate)
      span.progress-label.block.fz14.text-center.mt6 {{Math.floor(loadingProgress)}}%
</template>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";

.wrap-item-asset-model-preview {
  position: relative;
  .wrap-loading {
    position: absolute;
    left: 0; top: 0;
    .progress-label {
      color: $deactive_color;
    }
  }
}
</style>

<script>
import studioManager from '@/components/mixin/studioManager'
import vrmManager from '@/components/mixin/vrmManager'
import gltfManager from '@/components/mixin/gltfManager'
import * as THREE from 'three'
import { VRM, VRMSchema } from '@pixiv/three-vrm'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'

export default {
  mixins: [studioManager, vrmManager, gltfManager],
  props: {
    asset: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      renderer: null, scene: null, clock: null,
      camera: null, orbitControls: null,
      ambientLight: null,
      gltf: null,
      lineGroup: null, lineSegmentsThrehold: 10,
      loadingProgress: 0,
      canvasSize: null,
      onResizeTimer: null,
      isLoading: false
    }
  },
  mounted () {
    window.addEventListener('resize', () => {
      if (this.onResizeTimer) clearTimeout(this.onResizeTimer)
      this.onResizeTimer = setTimeout(this.onResize, 400)
    })
    this.canvasSize = {
      width: this.$el.clientWidth,
      height: this.$el.clientHeight
    }
    this.initializeScene()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
    this.disposeScene({ renderer: this.renderer, scene: this.scene })
  },
  methods: {
    async initializeScene () {
      this.scene = new THREE.Scene()
      this.clock = new THREE.Clock()

      // レンダラーの設定
      this.renderer = this.createRenderer({ 
        size: { width: this.canvasSize.width, height: this.canvasSize.height }
      })
      this.$refs.wrapCanvas.appendChild(this.renderer.domElement)

      // カメラの設定
      const cameraFov = this.asset.camera.fov ? 
        this.asset.camera.fov : this.defaultCameraFov
      this.camera = new THREE
        .PerspectiveCamera(cameraFov, this.canvasSize.width / this.canvasSize.height, 0.001, 1000)
      if (this.asset.camera) {
        const vec3Pos = this.asset.camera.position
        this.camera.position.set(vec3Pos.x, vec3Pos.y, vec3Pos.z)
        // this.camera.position = this.toVector3(vec3Pos)
      } else {
        this.camera.position.set(0, 0.0, 3.5)
      }

      // PostProcessing by mixim
      this.initializeComposer({
        renderer: this.renderer, scene: this.scene, camera: this.camera
      })

      // カメラコントーロールの設定
      this.orbitControls = new OrbitControls(this.camera, this.renderer.domElement)
      if (this.asset.defaultOrbitControls) {
        const vec3Target = this.asset.defaultOrbitControls.target
        this.orbitControls.target.set(vec3Target.x, vec3Target.y, vec3Target.z)
        this.orbitControls.target0.set(vec3Target.x, vec3Target.y, vec3Target.z)
        // this.orbitControls.target = this.toVector3(vec3Target)
      } else {
        this.orbitControls.target.set(0, 0, 0)
      }
      this.orbitControls.screenSpacePanning = true
      this.orbitControls.update()

      // ライトの設定
      const light = new THREE.DirectionalLight(0xffffff)
      light.position.set(1, 10, 1).normalize()
      light.intensity = this.defaultDirectionalLightIntensity
      this.scene.add(light)

      this.ambientLight = new THREE.AmbientLight(0xffffff)
      this.ambientLight.intensity = this.asset.defaultAmbientLight ?
        this.asset.defaultAmbientLight.intensity
        : this.defaultAmbientLightIntensity
      this.scene.add(this.ambientLight)
      
      if (['actor'].includes(this.asset.type)) {
        await this.addVRM()
      } else if (['3d_stage', '3d_object'].includes(this.asset.type)) {
        await this.addGLTF()
      }

      // for test
      // const boxGeometry = new THREE.BoxGeometry(0.5, 0.5, 0.5)
      // const boxMaterial = new THREE.MeshBasicMaterial({color: 0xff0000})
      // const box = new THREE.Mesh(boxGeometry, boxMaterial)
      // box.position.set(0.0, 0.0, 0.0)
      // this.scene.add(box)

      // アニメーションを開始
      this.tick()
    },
    tick () {
      requestAnimationFrame(this.tick)
      this.composer.render(this.scene, this.camera)
      // this.renderer.render(this.scene, this.camera)
    },
    async addVRM () {
      this.isLoading = true

      const progressFunc = (progress) => {
        this.loadingProgress = 100.0 * (progress.loaded / progress.total)
      }
      const errorFunc = (error) => {
        alert(error.type)
        alert(error.type)
        this.isLoading = false
      }
      const gltf = await this.loadGLTFwithDracoLoader({ 
        path: this.asset.assetURL, progressFunc, errorFunc
      })
      this.removeUnnecessaryElementsFromVRM({ gltf })

      const vrm = gltf.userData.vrm
      // this.vrm = await VRM.from(gltf).then(vrm => vrm)

      this.registerCustomExpression(vrm)

      // const pose = JSON.parse(JSON.stringify(this.initialVrmPose))
      // const blendShape = JSON.parse(JSON.stringify(this.initialVrmBlendShape))
      // this.updatePose({ vrm, pose })
      // this.updateBlendShape({ vrm, blendShape })

      // materialのアウトラインなどの調整
      const delta = this.clock.getDelta()
      this.setOutlineToVRM({ 
        vrm,
        materials: gltf.userData.vrmMToonMaterials,
        delta,
        outlineWidth: 0.0015,
        // outlineWidth: { cloth: 0.002, hair: 0.001, skin: 0.003 },
        color: { r: 0, g: 0, b: 0, a: 0 }
      })

      vrm.scene.userData.vrm = vrm
      this.scene.add(vrm.scene)

      if (this.isVRM1({ vrm })) {
        // this.vrm.scene.rotation.set(0, 3, 0)
      } else {
        vrm.scene.rotation.set(0, Math.PI, 0)
      }

      if (this.asset.photoPose) {
        const delta = this.clock.getDelta()
        this.updatePose({
          vrm,
          pose: this.asset.photoPose,
          delta
        })
      }
      if (this.asset.photoBlendShape) {
        const delta = this.clock.getDelta()
        this.updateBlendShape({ vrm, blendShape: this.asset.photoBlendShape, delta })
      }

      this.isLoading = false
    },
    async addGLTF () {
      this.isLoading = true

      const progressFunc = (progress) => {
        this.loadingProgress = 100.0 * (progress.loaded / progress.total)
      }
      const errorFunc = (error) => {
        alert(error.type)
        console.error(error)
        this.isLoading = false
      }
      const gltf = await this.loadGLTFwithDracoLoader({ 
        path: this.asset.assetURL, progressFunc, errorFunc
      })
      // gltf.scene = this.mergeObject(gltf.scene)
      gltf.scene = this.mergeObjectByModelSize({ object: gltf.scene })
      gltf.scene.traverse(node => node.applyOutline = true)

      let lineGroup
      if (this.asset.defaultLineSegmentsThrehold) {
        const edgeDegreeThrehold = this.asset.defaultLineSegmentsThrehold ?
          this.asset.defaultLineSegmentsThrehold
          : (100 - this.lineSegmentsThrehold)
        lineGroup = this.createLineGeometoryToGroup({
          group: gltf.scene, edgeDegreeThrehold
        })
      }
      
      // 初期サイズの設定
      const box3 = new THREE.Box3().setFromObject(gltf.scene)
      this.originalInitialSize = {
        w: box3.max.x - box3.min.x, h: box3.max.y - box3.min.y
      }
      
      const wSize = box3.max.x - box3.min.x
      const initialScale = (1.0 / wSize) * 2.0 // 1だと小さすぎるのでサイズを2.0しておく
      gltf.scene.scale.set(initialScale, initialScale, initialScale)
      
      // gltf.scene.position.set(0, 0, 0)
      // モデルのオフセットを設定
      this.positionOffset = box3.getCenter(new THREE.Vector3())
      gltf.scene.position.set(
        -this.positionOffset.x * initialScale,
        -this.positionOffset.y * initialScale,
        -this.positionOffset.z * initialScale
      )

      if (lineGroup) {
        // lineGroupのスケールはgltfのスケールが設定されてから合わせないとbox3の関係で大きさがずれる
        lineGroup.position.copy(gltf.scene.position)
        lineGroup.scale.copy(gltf.scene.scale)
        lineGroup.rotation.copy(gltf.scene.rotation)
        this.scene.add(lineGroup)
      }

      if (this.asset.defaultEmissiveColor) {
        this.updateMaterialEmissive({
          object: gltf.scene,
          emissiveColor: this.asset.defaultEmissiveColor
        })
      }

      this.scene.add(gltf.scene)

      this.isLoading = false
    },
    onResize () {
      if (this.onResizeTimer) clearTimeout(this.onResizeTimer)
      this.onResizeTimer = setTimeout(this.resizeCanvas, 400)
    },
    resizeCanvas () {
      this.canvasSize = {
        width: this.$el.clientWidth,
        height: this.$el.clientHeight
      }
      if (this.renderer && this.camera) {
        // カメラのアスペクト比を正す
        this.camera.aspect = this.canvasSize.width / this.canvasSize.height
        this.camera.updateProjectionMatrix()

        this.renderer.setPixelRatio(window.devicePixelRatio)
        this.renderer.setSize(this.canvasSize.width, this.canvasSize.height)
        this.resizeComposer(this.canvasSize)
      }
    },
  }
}
</script>
